/**
 * Feature Request data model
 * Represents user-submitted feature requests with voting and admin moderation
 */

/**
 * Feature Request Status Flow:
 * 'pending' -> 'approved' | 'rejected' | 'under_review'
 * 'approved' -> 'in_progress' | 'completed' | 'declined'
 */
export type FeatureRequestStatus =
  | 'pending' // Awaiting admin approval (only visible to submitter + admin)
  | 'approved' // Visible to all users, can be voted on
  | 'under_review' // Admin is reviewing
  | 'rejected' // Rejected by admin (only visible to submitter + admin)
  | 'in_progress' // Being worked on
  | 'completed' // Implemented
  | 'declined'; // Won't implement (after approval)

/**
 * Feature Request Attachment
 */
export interface FeatureRequestAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  size: number; // bytes (max 5MB per file)
  blobPath: string;
  uploadedAt: string;
}

/**
 * Feature Request entity
 */
export interface FeatureRequest {
  id: string;
  tenantId: string; // Partition key ('global' for cross-tenant visibility)
  submitterId: string; // User who submitted
  submitterEmail: string; // Denormalized for display
  submitterName: string; // Denormalized for display

  title: string; // Max 200 chars
  description: string; // Max 5000 chars
  status: FeatureRequestStatus;

  attachments: FeatureRequestAttachment[]; // Max 5 attachments

  upvotes: number; // Denormalized count for sorting
  downvotes: number; // Denormalized count for sorting

  adminResponse?: string; // Admin response text
  adminResponderId?: string; // Admin who responded
  adminResponderName?: string;
  respondedAt?: string;

  approvedAt?: string;
  approvedBy?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  rejectionReason?: string;

  createdAt: string;
  updatedAt: string;
}

/**
 * Feature Request Vote entity
 * Tracks individual user votes on feature requests
 */
export interface FeatureRequestVote {
  id: string; // `${featureRequestId}_${userId}`
  tenantId: string; // 'global'
  featureRequestId: string;
  userId: string;
  voteType: 'up' | 'down';
  createdAt: string;
  updatedAt: string;
}

/**
 * Request/Response types
 */
export interface CreateFeatureRequestRequest {
  title: string;
  description: string;
  attachments?: FeatureRequestAttachment[];
}

export interface UpdateFeatureRequestStatusRequest {
  status: FeatureRequestStatus;
  reason?: string; // Required for 'rejected'
}

export interface AdminRespondRequest {
  response: string;
}

export interface VoteRequest {
  voteType: 'up' | 'down';
}

/**
 * List parameters
 */
export interface ListFeatureRequestsParams {
  sortBy?: 'upvotes' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  continuationToken?: string;
}

export interface AdminListFeatureRequestsParams extends ListFeatureRequestsParams {
  status?: FeatureRequestStatus;
}

/**
 * Feature Request limits
 */
export const FEATURE_REQUEST_LIMITS = {
  maxTitleLength: 200,
  minTitleLength: 5,
  maxDescriptionLength: 5000,
  minDescriptionLength: 20,
  maxAttachments: 5,
  maxAttachmentSizeMB: 5,
  maxResponseLength: 2000,
} as const;

/**
 * Statuses visible to public (non-submitter) users
 */
export const PUBLIC_FEATURE_REQUEST_STATUSES: FeatureRequestStatus[] = [
  'approved',
  'in_progress',
  'completed',
  'declined',
];

/**
 * Statuses that require admin action
 */
export const PENDING_FEATURE_REQUEST_STATUSES: FeatureRequestStatus[] = ['pending', 'under_review'];
